import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";

/**
 * MarkdownViewer - Component for rendering Markdown content with proper formatting
 *
 * @param {Object} props
 * @param {string} props.url - URL of the Markdown file to fetch and render
 * @param {string} props.content - Direct Markdown content to render (alternative to url)
 */
const MarkdownViewer = ({ url, content }) => {
  const [markdown, setMarkdown] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If direct content is provided, use that
    if (content) {
      setMarkdown(content);
      return;
    }

    // Otherwise fetch from URL
    if (url) {
      const fetchMarkdown = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch markdown: ${response.status}`);
          }
          const text = await response.text();
          setMarkdown(text);
          setError(null);
        } catch (err) {
          console.error("Error fetching markdown:", err);
          setError(err.message);
          setMarkdown("");
        } finally {
          setIsLoading(false);
        }
      };

      fetchMarkdown();
    }
  }, [url, content]);

  if (isLoading) {
    return <div className="p-4 text-gray-400">Loading markdown...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">Error loading markdown: {error}</div>
    );
  }

  return (
    <div className="markdown-viewer bg-[#1a1e21] text-white p-6 rounded-lg max-w-4xl mx-auto">
      <ReactMarkdown>{markdown}</ReactMarkdown>
      <style jsx global>{`
        .markdown-viewer h1 {
          font-size: 2rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #4db1e0;
        }
        .markdown-viewer h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #4db1e0;
        }
        .markdown-viewer h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .markdown-viewer p {
          margin-bottom: 1rem;
          line-height: 1.6;
        }
        .markdown-viewer ul,
        .markdown-viewer ol {
          margin-left: 2rem;
          margin-bottom: 1rem;
        }
        .markdown-viewer li {
          margin-bottom: 0.5rem;
        }
        .markdown-viewer a {
          color: #4db1e0;
          text-decoration: underline;
        }
        .markdown-viewer a:hover {
          color: #3da0cf;
        }
        .markdown-viewer blockquote {
          border-left: 4px solid #4db1e0;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
        }
        .markdown-viewer code {
          background-color: #2a2e35;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-family: monospace;
        }
        .markdown-viewer pre {
          background-color: #2a2e35;
          padding: 1rem;
          border-radius: 5px;
          overflow-x: auto;
          margin-bottom: 1rem;
        }
        .markdown-viewer pre code {
          background-color: transparent;
          padding: 0;
        }
        .markdown-viewer hr {
          border: 0;
          border-top: 1px solid #2f3236;
          margin: 1.5rem 0;
        }
        .markdown-viewer img {
          max-width: 100%;
          height: auto;
          border-radius: 5px;
        }
        .markdown-viewer strong {
          font-weight: 600;
        }
        .markdown-viewer em {
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default MarkdownViewer;
